import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { FaCheck, FaTimes, FaPhone, FaEnvelope, FaGlobe, FaMapMarkerAlt, FaCertificate } from 'react-icons/fa';

export default function DirectoryPending() {
  const { i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const [pending, setPending] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchPending();
      fetchCategories();
    }
  }, [user]);

  const fetchPending = async () => {
    try {
      const response = await axios.get(`${API}/directory/admin/pending?admin_id=${user.id}`);
      setPending(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching pending listings:', error);
      toast.error('Erreur lors du chargement');
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/directory/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleApprove = async (listingId) => {
    try {
      await axios.put(`${API}/directory/admin/approve/${listingId}?admin_id=${user.id}`);
      toast.success('Fiche approuvée !');
      fetchPending();
    } catch (error) {
      console.error('Error approving listing:', error);
      toast.error('Erreur lors de l\'approbation');
    }
  };

  const handleReject = async (listingId) => {
    if (!confirm('Rejeter cette fiche ?')) {
      return;
    }

    try {
      await axios.put(`${API}/directory/admin/reject/${listingId}?admin_id=${user.id}`);
      toast.success('Fiche rejetée');
      fetchPending();
    } catch (error) {
      console.error('Error rejecting listing:', error);
      toast.error('Erreur lors du rejet');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <p>Chargement...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 md:p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#0A2540] mb-2">
            Fiches en Attente de Validation
          </h1>
          <p className="text-gray-600">
            {pending.length} fiche(s) à valider
          </p>
        </div>

        {pending.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-gray-500">
              Aucune fiche en attente
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {pending.map(listing => (
              <Card key={listing.id} className="hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row gap-6">
                    {/* Main Info */}
                    <div className="flex-1 space-y-4">
                      {/* Header */}
                      <div>
                        <h2 className="text-2xl font-bold text-[#0A2540] mb-2">
                          {listing.company_name}
                        </h2>
                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                          <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full">
                            {categories.find(c => c.id === listing.category)?.[i18n.language === 'fr' ? 'name_fr' : 'name_en']}
                          </span>
                          {listing.company_size && (
                            <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full capitalize">
                              {listing.company_size}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <FaMapMarkerAlt className="mr-2 text-[#635BFF]" />
                          <span>{listing.address ? `${listing.address}, ` : ''}{listing.city}, {listing.country}</span>
                          {listing.postal_code && <span className="ml-2">({listing.postal_code})</span>}
                        </div>
                      </div>

                      {/* Description */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="font-semibold mb-2">Description (FR):</p>
                        <p className="text-gray-700">{listing.description_fr}</p>
                        {listing.description_en && (
                          <>
                            <p className="font-semibold mt-3 mb-2">Description (EN):</p>
                            <p className="text-gray-700">{listing.description_en}</p>
                          </>
                        )}
                      </div>

                      {/* Services */}
                      {listing.services && listing.services.length > 0 && (
                        <div>
                          <p className="font-semibold mb-2">Services:</p>
                          <div className="flex flex-wrap gap-2">
                            {listing.services.map(service => (
                              <span 
                                key={service} 
                                className="px-3 py-1 bg-[#635BFF]/10 text-[#5248d9] rounded-full text-sm capitalize"
                              >
                                {service}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Certifications */}
                      {listing.certifications && listing.certifications.length > 0 && (
                        <div className="flex items-center gap-2">
                          <FaCertificate className="text-[#635BFF]" />
                          <span className="text-sm font-semibold">Certifications:</span>
                          <span className="text-sm text-gray-600">
                            {listing.certifications.join(', ')}
                          </span>
                        </div>
                      )}

                      {/* Contact */}
                      <div className="border-t pt-4 space-y-2">
                        <div className="flex items-center text-sm">
                          <FaPhone className="mr-2 text-[#0A2540]" />
                          <a href={`tel:${listing.phone}`} className="hover:text-[#635BFF]">
                            {listing.phone}
                          </a>
                        </div>
                        <div className="flex items-center text-sm">
                          <FaEnvelope className="mr-2 text-[#0A2540]" />
                          <a href={`mailto:${listing.email}`} className="hover:text-[#635BFF]">
                            {listing.email}
                          </a>
                        </div>
                        {listing.website && (
                          <div className="flex items-center text-sm">
                            <FaGlobe className="mr-2 text-[#0A2540]" />
                            <a 
                              href={listing.website} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="hover:text-[#635BFF]"
                            >
                              {listing.website}
                            </a>
                          </div>
                        )}
                      </div>

                      {/* Date */}
                      <div className="text-xs text-gray-500">
                        Soumis le: {new Date(listing.created_at).toLocaleDateString('fr-FR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex md:flex-col gap-3 md:min-w-[160px]">
                      <Button
                        onClick={() => handleApprove(listing.id)}
                        className="flex-1 md:flex-none bg-[#635BFF] hover:bg-[#5248d9]"
                      >
                        <FaCheck className="mr-2" />
                        Approuver
                      </Button>
                      <Button
                        onClick={() => handleReject(listing.id)}
                        variant="destructive"
                        className="flex-1 md:flex-none"
                      >
                        <FaTimes className="mr-2" />
                        Rejeter
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
